import { App, Flex } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { Loading, QueryErrorAlert } from 'src/components'
import { useDocumentTitle, useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'

import { ArchiveRoleModal } from '../../components/ArchiveRoleModal'
import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'
import { isOwnerRole } from '../WorkspaceRolesPage/constants'
import {
  useRoleMembers,
  useWorkspaceRole,
  useWorkspaceRoles
} from '../WorkspaceRolesPage/hooks'
import { RoleDrawer } from '../WorkspaceRolesPage/RoleDrawer'
import type { RoleFormValues } from '../WorkspaceRolesPage/types'

import { RoleDetailCard } from './RoleDetailCard'
import { RoleMembersTable } from './RoleMembersTable'

export const WorkspaceRoleDetailPage = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const navigate = useNavigate()
  const { id, workspaceId, idValid } = useWorkspaceRoute()
  const { roleId: roleIdParam = '' } = useParams<{ roleId: string }>()
  const roleId = Number(roleIdParam)
  const roleIdValid = Number.isFinite(roleId) && roleId > 0

  const {
    data: role,
    isLoading,
    isError,
    refetch
  } = useWorkspaceRole(roleIdValid ? roleId : null, {
    enabled: idValid && roleIdValid
  })

  useDocumentTitle(role?.name)

  const mayManageRoles = useHasWorkspacePermissions(
    WorkspacePermissionCode.ROLES_MANAGE
  )

  const { roles, updateRole, replaceAndDelete, isMutating } = useWorkspaceRoles(
    idValid ? workspaceId : null
  )

  const { members: roleMembers } = useRoleMembers(
    idValid ? workspaceId : null,
    roleIdValid ? roleId : null
  )

  const members = useMemo(
    () =>
      roleMembers.map((m) => ({
        id: m.id,
        name: m.name,
        email: m.email,
        team: m.team,
        assignedAt: m.joinedDate,
        source: m.source,
        sourceGroupName: m.sourceGroupName
      })),
    [roleMembers]
  )

  const replacementCandidates = useMemo(
    () =>
      role
        ? roles.filter((r) => r.id !== role.id && !isOwnerRole(r.codename))
        : [],
    [roles, role]
  )

  const [editOpen, setEditOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)

  if (!idValid || !roleIdValid) {
    return <Navigate to={paths.workspaces.roles(id)} replace />
  }

  if (isLoading) {
    return (
      <WorkspacePageShell>
        <Loading />
      </WorkspacePageShell>
    )
  }

  if (isError || !role) {
    return (
      <WorkspacePageShell>
        <Flex vertical gap={16} style={{ padding: '24px 0' }}>
          <QueryErrorAlert onRetry={() => void refetch()} />
        </Flex>
      </WorkspacePageShell>
    )
  }

  const handleEditSubmit = async (values: RoleFormValues) => {
    try {
      await updateRole(role.id, values)
      void message.success(t('workspace.orgRoles.messages.updated'))
      setEditOpen(false)
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleArchiveConfirm = async (replacementRoleId: number) => {
    try {
      await replaceAndDelete(role.id, replacementRoleId)
      void message.success(t('workspace.orgRoles.messages.replaced'))
      setArchiveOpen(false)
      navigate(paths.workspaces.roles(id))
    } catch (error) {
      showApiErrors(error)
    }
  }

  return (
    <WorkspacePageShell breadcrumbLeaf={role.name}>
      <Flex vertical gap={16}>
        <RoleDetailCard
          role={role}
          memberCount={role.users_count}
          canManage={mayManageRoles}
          onEdit={() => setEditOpen(true)}
          onArchive={() => setArchiveOpen(true)}
        />
        <RoleMembersTable members={members} />
      </Flex>

      <RoleDrawer
        open={editOpen}
        mode="edit"
        role={role}
        saving={isMutating}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSubmit}
      />

      <ArchiveRoleModal
        open={archiveOpen}
        roleName={role.name}
        memberCount={role.users_count}
        availableRoles={replacementCandidates.map((r) => ({
          id: r.id,
          name: r.name
        }))}
        confirmLoading={isMutating}
        onConfirm={handleArchiveConfirm}
        onCancel={() => setArchiveOpen(false)}
      />
    </WorkspacePageShell>
  )
}
